import Vue from 'vue';
import App from './App.vue';
import router from './router';
import toastService from './services/toastService';

Vue.config.productionTip = false;

function inferToastType(message, fallback = 'info') {
  const text = String(message || '').toLowerCase();

  if (/fail|error|invalid|missing|required|denied|unable|not found|network/.test(text)) {
    return 'error';
  }

  if (/warn|warning|already|limit|exceed|hold|careful/.test(text)) {
    return 'warning';
  }

  if (/success|created|updated|deleted|uploaded|approved|assigned|submitted|enabled|disabled|ready|saved/.test(text)) {
    return 'success';
  }

  return fallback;
}

Vue.prototype.$toast = toastService;
Vue.prototype.$notify = function notify(message, type = 'info', duration) {
  toastService.show(message, type, duration);
};

Vue.mixin({
  created() {
    this.__globalToastUnwatchers = [];

    if (!this.$data || typeof this.$data !== 'object') {
      return;
    }

    const watchedKeys = ['message', 'successMessage', 'errorMessage', 'statusMessage', 'error', 'notification'];

    watchedKeys.forEach((key) => {
      if (!(key in this.$data)) {
        return;
      }

      const unwatch = this.$watch(key, (value) => {
        if (!value) {
          return;
        }

        let message = value;
        let type = 'info';

        if (key === 'successMessage') {
          type = 'success';
        } else if (key === 'errorMessage' || key === 'error') {
          type = 'error';
        } else if (key === 'statusMessage') {
          type = this.statusType || inferToastType(value, 'info');
        } else if (key === 'notification' && typeof value === 'object') {
          message = value.message;
          type = value.type || inferToastType(value.message, 'info');
        } else {
          type = inferToastType(value, 'info');
        }

        if (!message) {
          return;
        }

        toastService.show(message, type);

        if (key === 'notification') {
          if (this.notification === value) {
            this.notification = null;
          }
          return;
        }

        if (this[key] === value) {
          this[key] = '';
        }
      });

      this.__globalToastUnwatchers.push(unwatch);
    });
  },
  beforeDestroy() {
    if (!Array.isArray(this.__globalToastUnwatchers)) {
      return;
    }

    this.__globalToastUnwatchers.forEach((unwatch) => {
      if (typeof unwatch === 'function') {
        unwatch();
      }
    });
    this.__globalToastUnwatchers = [];
  },
});

async function cleanupLegacyBrowserWorkers() {
  if (typeof window === 'undefined') return;

  if ('serviceWorker' in navigator) {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(registration => registration.unregister()));
    } catch (error) {
      // Ignore browser cleanup failures so app bootstrap continues.
    }
  }

  if ('caches' in window) {
    try {
      const cacheKeys = await window.caches.keys();
      await Promise.all(cacheKeys.map(cacheKey => window.caches.delete(cacheKey)));
    } catch (error) {
      // Ignore cache cleanup failures so app bootstrap continues.
    }
  }
}

cleanupLegacyBrowserWorkers().finally(() => {
  new Vue({
    router,
    render: h => h(App),
  }).$mount('#app');
});
